import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable, throwError } from 'rxjs';
import { LoggerService } from './logger.service';
import { ToastService } from './toast.service';
import { OrderAccessGroupResponse } from '../shared/models/groups/order-access-group-response.model';
import { KeycloakUserResponse } from '../shared/models/groups/keycloak-user-response.model';
import { UserRoleAssignmentPayload } from '../shared/models/access-control/user-role-assignment-payload.model';
import { AddressResponse } from '../shared/models/order/address-response.model';
import { OrderTitleResponse } from '../shared/models/order/order-title-response.model';
import { GroupDetailResponse } from '../shared/models/groups/group-detail-response.model';
import { SaveGroupPayload } from '../shared/models/access-control/save-group-payload.model';
import { UpdateGroupPayload } from '../shared/models/access-control/update-group-payload.model';
import { GroupOrderSelectionPageResponse } from '../shared/models/groups/group-order-selection-page-response.model';
import { GroupUserSelectionPageResponse } from '../shared/models/groups/group-user-selection-page-response.model';
import { GroupOrderSelectionSearchPayload } from '../shared/models/groups/group-order-selection-search-payload.model';
import { GroupUserSelectionSearchPayload } from '../shared/models/groups/group-user-selection-search-payload.model';

@Injectable({ providedIn: 'root' })
export class CustomerFrontendManagementService {
  private readonly _apiUrlCustomerAccess = `${
    import.meta.env.NG_APP_API_URL
  }/backoffice/v1/customerAccess`;

  constructor(
    private http: HttpClient,
    private loggerService: LoggerService,
    private toastService: ToastService
  ) {}

  getPaginatedGroups(params: HttpParams): Observable<{ content: OrderAccessGroupResponse[]; totalPages: number }> {
    return this.http
      .get<{ content: OrderAccessGroupResponse[]; totalPages: number }>(
        `${this._apiUrlCustomerAccess}/groups`,
        { params: this.withOrFilterCombination(params) }
      )
      .pipe(catchError(this.handleError.bind(this)));
  }

  getGroupsByCustomer(customerId: string): Observable<OrderAccessGroupResponse[]> {
    return this.http
      .get<OrderAccessGroupResponse[]>(
        `${this._apiUrlCustomerAccess}/${customerId}/groups`
      )
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * Load all users from access_control.keycloak_users for the given customer
   */
  getUsersByCustomer(customerId: string): Observable<KeycloakUserResponse[]> {
    return this.http
      .get<KeycloakUserResponse[]>(
        `${this._apiUrlCustomerAccess}/${customerId}/users`
      )
      .pipe(catchError(this.handleError.bind(this)));
  }

  getPaginatedUsersByCustomer(
    customerId: string,
    payload: GroupUserSelectionSearchPayload
  ): Observable<GroupUserSelectionPageResponse> {
    return this.http
      .post<GroupUserSelectionPageResponse>(
        `${this._apiUrlCustomerAccess}/${customerId}/users/paged/search`,
        this.withOrFilterCombinationPayload(payload)
      )
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * Create a new entry in access_control.user_x_role
   */
  addUserRoleAssignment(
    payload: UserRoleAssignmentPayload
  ): Observable<any> {
    return this.http
      .post(`${this._apiUrlCustomerAccess}/${payload.customerId}/user-x-role`, payload)
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * Load all customers (addresses used as customers in orders)
   */
  getAllCustomers(): Observable<AddressResponse[]> {
    return this.http
      .get<AddressResponse[]>(`${this._apiUrlCustomerAccess}/customers`)
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * Load all order titles (order numbers) for the given customer
   */
  getOrderTitlesByCustomer(customerId: string): Observable<OrderTitleResponse[]> {
    return this.http
      .get<OrderTitleResponse[]>(
        `${this._apiUrlCustomerAccess}/${customerId}/orders`
      )
      .pipe(catchError(this.handleError.bind(this)));
  }

  getPaginatedOrdersByCustomer(
    customerId: string,
    payload: GroupOrderSelectionSearchPayload
  ): Observable<GroupOrderSelectionPageResponse> {
    return this.http
      .post<GroupOrderSelectionPageResponse>(
        `${this._apiUrlCustomerAccess}/${customerId}/orders/paged/search`,
        this.withOrFilterCombinationPayload(payload)
      )
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * Create a new group and assign one order to it. Optionally assign a user role in the same request.
   */
  createGroup(payload: SaveGroupPayload): Observable<any> {
    return this.http
      .post(`${this._apiUrlCustomerAccess}/${payload.customerId}/groups/add`, payload)
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * Update a group and optionally assign one user role.
   */
  updateGroup(payload: UpdateGroupPayload): Observable<any> {
    return this.http
      .put(`${this._apiUrlCustomerAccess}/${payload.customerId}/groups/update/${payload.groupId}`, payload)
      .pipe(catchError(this.handleError.bind(this)));
  }

  deleteGroup(customerId: string, groupId: string): Observable<any> {
    return this.http
      .delete(`${this._apiUrlCustomerAccess}/${customerId}/groups/delete/${groupId}`)
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * Load details for a group: order title and assigned users.
   */
  getGroupDetails(customerId: string, groupId: string): Observable<GroupDetailResponse> {
    return this.http
      .get<GroupDetailResponse>(`${this._apiUrlCustomerAccess}/${customerId}/groups/${groupId}`)
      .pipe(catchError(this.handleError.bind(this)));
  }

  private withOrFilterCombination(params: HttpParams): HttpParams {
    return params.has('filterCombination')
      ? params
      : params.set('filterCombination', 'OR');
  }

  private withOrFilterCombinationPayload<
    T extends { filterCombination?: 'AND' | 'OR' }
  >(payload: T): T {
    return payload.filterCombination
      ? payload
      : { ...payload, filterCombination: 'OR' };
  }

  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'Ein unbekannter Fehler ist aufgetreten!';
    if (error.error instanceof ErrorEvent) {
      errorMessage = `Ein Fehler ist aufgetreten: ${error.error.message}`;
    } else {
      if (error.status === 409) {
        errorMessage = 'Für diesen Kunden existiert bereits eine Gruppe mit diesem Namen.';
      } else {
        errorMessage =
          `Backend hat mit Status ${error.status} geantwortet. ` +
          `Grund dafür ist: ${error.message}`;
      }
    }
    this.loggerService.error(errorMessage);
    this.toastService.show('warn', 'Ein Fehler ist aufgetreten', errorMessage);
    return throwError(() => new Error(errorMessage));
  }
}
